<script>
  import { projectLoan, annualRepayment } from "$calc/plans.js";
  import {
    pensionScenario,
    marginalRates,
    projectPot,
  } from "$calc/pension.js";
  import taxYear from "$data/2025-26.json";
  import { graduate } from "./appState.svelte.js";
  import SegmentBar from "./SegmentBar.svelte";

  const PLANS = [
    { key: "plan1", label: "Plan 1" },
    { key: "plan2", label: "Plan 2" },
    { key: "plan3", label: "Plan 3 (postgrad)" },
    { key: "plan4", label: "Plan 4 (Scotland)" },
    { key: "plan5", label: "Plan 5" },
  ];

  const METHODS = [
    { key: "salarySacrifice", label: "Salary sacrifice (best if offered)" },
    { key: "reliefAtSource", label: "Relief at source (basic-rate)" },
  ];

  // Local state — kept local because these don't affect the loan tab
  let currentPot = $state(0);
  // age now on shared graduate.currentAge
  let retirementAge = $state(68);
  let realReturn = $state(4.5);
  let salaryGrowth = $state(3);

  const yearsToRetire = $derived(Math.max(0, retirementAge - graduate.currentAge));

  const band = $derived(taxYear.incomeTax.englandNIWales);
  const niData = $derived(taxYear.nationalInsurance.class1Employee);
  const planData = $derived(taxYear.studentLoans[graduate.planKey]);

  // Auto-detect marginal rates from current salary
  const marginal = $derived(marginalRates(graduate.salary, band, niData));

  // With pension — one year's scenario
  const withScenario = $derived(
    pensionScenario({
      salary: graduate.salary,
      method: graduate.pensionMethod,
      employeePct: graduate.pensionEmployeePct,
      employerBasePct: graduate.pensionEmployerBasePct,
      employerMatchRatio: graduate.pensionEmployerMatchRatio,
      employerMatchCapPct: graduate.pensionEmployerMatchCapPct,
      marginalTaxRate: marginal.tax,
      marginalNIRate: marginal.ni,
    }),
  );

  // Without pension — no employee contribution.
  // Assume employer stops the base if the employee opts out (worst case;
  // some employers pay it anyway, but auto-enrolment presumes yes-contrib).
  const withoutScenario = $derived(
    pensionScenario({
      salary: graduate.salary,
      method: graduate.pensionMethod,
      employeePct: 0,
      employerBasePct: 0,
      employerMatchRatio: 0,
      employerMatchCapPct: 0,
      marginalTaxRate: marginal.tax,
      marginalNIRate: marginal.ni,
    }),
  );

  // Pot projections to retirement
  const potWith = $derived(
    projectPot({
      startingPot: Math.max(0, currentPot),
      annualContribution: withScenario.totalIntoPension,
      years: yearsToRetire,
      realReturn: realReturn / 100,
      contributionGrowth: salaryGrowth / 100,
    }),
  );

  const potWithout = $derived(
    projectPot({
      startingPot: Math.max(0, currentPot),
      annualContribution: 0,
      years: yearsToRetire,
      realReturn: realReturn / 100,
    }),
  );

  // Intermediate scenario: only employer contributions (base + match).
  // Used to attribute the opportunity-cost gap between employer share
  // (unrecoverable if you opt out) and employee share (offset by keeping
  // the net cost as take-home today).
  const potEmployerOnly = $derived(
    projectPot({
      startingPot: Math.max(0, currentPot),
      annualContribution: withScenario.employerBase + withScenario.employerMatch,
      years: yearsToRetire,
      realReturn: realReturn / 100,
      contributionGrowth: salaryGrowth / 100,
    }),
  );

  const potDelta = $derived(potWith.closingPot - potWithout.closingPot);
  const employerGap = $derived(potEmployerOnly.closingPot - potWithout.closingPot);
  const employeeGap = $derived(potWith.closingPot - potEmployerOnly.closingPot);

  // Own vs employer contributions (cumulative over the career)
  // For simplicity, assume contributions grow with salary but ratio stays constant.
  const employeeTotalNominal = $derived(
    withScenario.employeeGross * yearsForContribGrowth(yearsToRetire, salaryGrowth / 100),
  );
  const employerTotalNominal = $derived(
    (withScenario.employerBase + withScenario.employerMatch) *
      yearsForContribGrowth(yearsToRetire, salaryGrowth / 100),
  );
  const growthTotal = $derived(potWith.closingPot - employeeTotalNominal - employerTotalNominal - Math.max(0, currentPot));

  function yearsForContribGrowth(years, growth) {
    // Sum of (1+growth)^i for i=0..years-1 — approximates cumulative
    // contributions under salary growth. Same as an annuity sum.
    if (growth === 0) return years;
    return (Math.pow(1 + growth, years) - 1) / growth;
  }

  // Loan trajectory — with pension (salary reduced by sacrifice) vs without
  const slcRepaymentWith = $derived((s) =>
    annualRepayment(
      graduate.pensionMethod === "salarySacrifice" ? s - withScenario.employeeGross * (s / graduate.salary || 1) : s,
      planData,
    ),
  );

  // Simplest: build a projectLoan-friendly override by adjusting starting salary.
  const loanWith = $derived(
    projectLoan({
      startingBalance: Math.max(0, graduate.loanBalance),
      startingSalary: withScenario.slcRelevantSalary,
      salaryGrowth: salaryGrowth / 100,
      rpiMarch: taxYear.assumedIndices.rpiMarchDefault,
      bankRate: taxYear.assumedIndices.bankRateDefault,
      planData,
      ageAtStart: planData.wipeAtAge != null ? graduate.currentAge : null,
    }),
  );
  const loanWithout = $derived(
    projectLoan({
      startingBalance: Math.max(0, graduate.loanBalance),
      startingSalary: Math.max(0, graduate.salary),
      salaryGrowth: salaryGrowth / 100,
      rpiMarch: taxYear.assumedIndices.rpiMarchDefault,
      bankRate: taxYear.assumedIndices.bankRateDefault,
      planData,
      ageAtStart: planData.wipeAtAge != null ? graduate.currentAge : null,
    }),
  );

  const slcSavedByPension = $derived(loanWithout.totalRepaid - loanWith.totalRepaid);

  const fmtGBP = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(n);
  const fmtGBP2 = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  const fmtPct = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "percent",
      maximumFractionDigits: 1,
    }).format(n);
</script>

<section class="screen">
  <div class="intro">
    <h2>Pension optimiser</h2>
    <p>
      What the employer match is really worth, what it costs from your
      take-home, and what happens to your student loan when you sacrifice
      salary into a pension. All numbers on a real-return basis (already
      inflation-adjusted).
    </p>
  </div>

  <div class="layout">
    <aside class="inputs card">
      <h3>Shared with other tabs</h3>
      <div class="shared-block">
        <div>Salary <strong>{fmtGBP(graduate.salary)}</strong></div>
        <div>Plan <strong>{PLANS.find(p => p.key === graduate.planKey)?.label}</strong></div>
        <div>Loan balance <strong>{fmtGBP(graduate.loanBalance)}</strong></div>
        <div class="marginal">
          Marginal on next £1:
          <strong>{fmtPct(marginal.tax)}</strong> tax +
          <strong>{fmtPct(marginal.ni)}</strong> NI
        </div>
      </div>

      <h3 class="section-heading">Pension setup</h3>

      <label class="field">
        <span class="label">Method</span>
        <select bind:value={graduate.pensionMethod}>
          {#each METHODS as m (m.key)}
            <option value={m.key}>{m.label}</option>
          {/each}
        </select>
      </label>

      <label class="field">
        <span class="label">Your contribution</span>
        <div class="suffix-input">
          <input type="number" min="0" max="60" step="0.5" bind:value={graduate.pensionEmployeePct} />
          <span class="suffix">% of salary</span>
        </div>
        <span class="hint">= {fmtGBP(withScenario.employeeGross)} / year gross</span>
      </label>

      <h3 class="section-heading">Employer</h3>

      <label class="field">
        <span class="label">Baseline contribution</span>
        <div class="suffix-input">
          <input type="number" min="0" max="30" step="0.5" bind:value={graduate.pensionEmployerBasePct} />
          <span class="suffix">% of salary</span>
        </div>
        <span class="hint">Auto-enrolment minimum is 3%</span>
      </label>

      <label class="field">
        <span class="label">Match ratio</span>
        <div class="suffix-input">
          <input type="number" min="0" max="3" step="0.25" bind:value={graduate.pensionEmployerMatchRatio} />
          <span class="suffix">:1</span>
        </div>
        <span class="hint">
          {graduate.pensionEmployerMatchRatio === 0 ? "No match" :
            graduate.pensionEmployerMatchRatio === 1 ? "£1 for every £1 you put in" :
            `£${graduate.pensionEmployerMatchRatio} for every £1 you put in`}
        </span>
      </label>

      <label class="field">
        <span class="label">Match cap</span>
        <div class="suffix-input">
          <input type="number" min="0" max="30" step="0.5" bind:value={graduate.pensionEmployerMatchCapPct} />
          <span class="suffix">% of salary</span>
        </div>
        <span class="hint">Up to this % of your salary is matched</span>
      </label>

      <h3 class="section-heading">Projection assumptions</h3>

      <label class="field">
        <span class="label">Current age</span>
        <input type="number" min="16" max="70" step="1" bind:value={graduate.currentAge} />
        <span class="hint-sync">Shared with Uni &amp; Dashboard</span>
      </label>

      <label class="field">
        <span class="label">Retirement age</span>
        <input type="number" min={graduate.currentAge} max="80" step="1" bind:value={retirementAge} />
        <span class="hint">{yearsToRetire} years to project</span>
      </label>

      <label class="field">
        <span class="label">Current pension pot</span>
        <div class="prefix-input">
          <span class="prefix">£</span>
          <input type="number" min="0" step="500" bind:value={currentPot} />
        </div>
      </label>

      <label class="field">
        <span class="label">Real return (net of inflation)</span>
        <div class="suffix-input">
          <input type="number" min="-5" max="15" step="0.1" bind:value={realReturn} />
          <span class="suffix">%</span>
        </div>
      </label>

      <label class="field">
        <span class="label">Salary growth (real)</span>
        <div class="suffix-input">
          <input type="number" min="-5" max="10" step="0.1" bind:value={salaryGrowth} />
          <span class="suffix">%</span>
        </div>
      </label>
    </aside>

    <section class="output">
      <!-- Headline: pot at retirement -->
      <div class="headline card">
        <div class="outcome-label">Pension pot at age {retirementAge}</div>
        <div class="big-figure">{fmtGBP(potWith.closingPot)}</div>
        <div class="secondary-figures">
          in today's money · {yearsToRetire} years to grow
        </div>
      </div>

      <!-- Where the pot comes from -->
      <div class="split card">
        <h3>Where the pot comes from</h3>
        <div class="split-bar-wrap">
          <SegmentBar
            height={28}
            segments={[
              {
                label: "Your contributions",
                value: employeeTotalNominal,
                color: "var(--accent)",
                hint: `Net cost ${fmtGBP(withScenario.employeeNetCost)}/yr today`,
              },
              {
                label: "Employer contributions",
                value: employerTotalNominal,
                color: "var(--success)",
                hint: `${fmtGBP(withScenario.employerBase)}/yr base + ${fmtGBP(withScenario.employerMatch)}/yr match today`,
              },
              {
                label: "Compound growth",
                value: Math.max(0, growthTotal),
                color: "var(--warning)",
                hint: `${fmtPct(realReturn / 100)} real return`,
              },
            ]}
          />
          <ul class="split-legend legend-below">

            <li>
              <span class="dot dot-you"></span>
              <div>
                <div class="split-heading">Your contributions</div>
                <div class="split-detail">
                  {fmtGBP(employeeTotalNominal)}
                  &middot; net cost {fmtGBP(withScenario.employeeNetCost)}/yr today
                </div>
              </div>
            </li>
            <li>
              <span class="dot dot-employer"></span>
              <div>
                <div class="split-heading">
                  Employer contributions
                  {#if withScenario.employerMatch > 0}
                    <span class="tag tag-free">free money</span>
                  {/if}
                </div>
                <div class="split-detail">
                  {fmtGBP(employerTotalNominal)}
                  &middot; {fmtGBP(withScenario.employerBase)}/yr base +
                  {fmtGBP(withScenario.employerMatch)}/yr match today
                </div>
              </div>
            </li>
            <li>
              <span class="dot dot-growth"></span>
              <div>
                <div class="split-heading">Compound growth</div>
                <div class="split-detail">
                  {fmtGBP(Math.max(0, growthTotal))}
                  &middot; {fmtPct(realReturn / 100)} real return
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div class="return-note">
          Every £1 of net take-home you sacrifice puts
          <strong>{withScenario.returnMultiple.toFixed(2)}×</strong>
          into the pension today, before any growth.
        </div>
      </div>

      <!-- Opportunity cost -->
      <div class="opp-cost card">
        <div class="opp-header">
          <div>
            <div class="opp-label">Opportunity cost of paying 0%</div>
            <div class="opp-value">{fmtGBP(potDelta)}</div>
            <div class="opp-sub">
              smaller pot at age {retirementAge}
            </div>
          </div>
          <div class="opp-comparison">
            <div class="opp-row">
              <span>Contributing {graduate.pensionEmployeePct}%:</span>
              <strong>{fmtGBP(potWith.closingPot)}</strong>
            </div>
            <div class="opp-row">
              <span>Opting out (0%):</span>
              <strong>{fmtGBP(potWithout.closingPot)}</strong>
            </div>
          </div>
        </div>

        <!-- Split the gap by attribution -->
        <div class="opp-split">
          <div class="opp-split-header">
            <span>Of that gap</span>
          </div>
          <SegmentBar
            height={14}
            segments={[
              {
                label: "From employer contributions",
                value: Math.max(0, employerGap),
                color: "var(--danger)",
                hint: "Money that only exists if you're in the scheme — unrecoverable if you opt out.",
              },
              {
                label: "From your own contributions",
                value: Math.max(0, employeeGap),
                color: "var(--warning)",
                hint: "Your own gross contributions plus growth on them, minus the tax + NI you'd have paid on the equivalent take-home.",
              },
            ]}
          />
          <div class="opp-split-rows">
            <div class="opp-split-row">
              <div class="opp-split-swatch opp-split-swatch-employer"></div>
              <div class="opp-split-row-body">
                <div class="opp-split-row-heading">
                  From employer contributions
                  <span class="tag tag-loss">unrecoverable</span>
                </div>
                <div class="opp-split-row-detail">
                  <strong>{fmtGBP(employerGap)}</strong>
                  &middot; {fmtPct(employerGap / (potDelta || 1))} of the gap.
                  {#if withScenario.employerMatch > 0}
                    Includes {fmtGBP(withScenario.employerMatch)}/yr of match plus
                    {fmtGBP(withScenario.employerBase)}/yr of baseline — money that
                    only exists if you're in the scheme.
                  {:else}
                    Baseline {fmtGBP(withScenario.employerBase)}/yr — you get nothing
                    if you opt out.
                  {/if}
                </div>
              </div>
            </div>
            <div class="opp-split-row">
              <div class="opp-split-swatch opp-split-swatch-employee"></div>
              <div class="opp-split-row-body">
                <div class="opp-split-row-heading">
                  From your own contributions
                  <span class="tag tag-neutral">offset partly</span>
                </div>
                <div class="opp-split-row-detail">
                  <strong>{fmtGBP(employeeGap)}</strong>
                  &middot; {fmtPct(employeeGap / (potDelta || 1))} of the gap.
                  Contributing {fmtGBP(withScenario.employeeGross)}/yr gross at
                  a net cost of {fmtGBP(withScenario.employeeNetCost)}/yr.
                  If you opted out, you'd keep the net cost as take-home but
                  lose the tax + NI relief and the growth on it.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Student-loan impact -->
      <div class="slc-impact card">
        <h3>Impact on your student loan</h3>
        {#if graduate.pensionMethod === "salarySacrifice"}
          <p class="slc-explain">
            Salary sacrifice lowers the salary SLC sees for repayment purposes,
            so annual repayments fall by 9% × your contribution.
          </p>
          <div class="slc-compare">
            <div class="slc-col">
              <div class="slc-col-label">With {graduate.pensionEmployeePct}% sal-sac</div>
              <div class="slc-col-outcome slc-{loanWith.outcome}">
                {loanWith.outcome === "cleared"
                  ? `Clears in ${loanWith.yearsToOutcome} yrs`
                  : `Wipes at yr ${loanWith.yearsToOutcome}`}
              </div>
              <div class="slc-col-total">
                Total repaid <strong>{fmtGBP(loanWith.totalRepaid)}</strong>
              </div>
            </div>
            <div class="slc-col">
              <div class="slc-col-label">Without pension</div>
              <div class="slc-col-outcome slc-{loanWithout.outcome}">
                {loanWithout.outcome === "cleared"
                  ? `Clears in ${loanWithout.yearsToOutcome} yrs`
                  : `Wipes at yr ${loanWithout.yearsToOutcome}`}
              </div>
              <div class="slc-col-total">
                Total repaid <strong>{fmtGBP(loanWithout.totalRepaid)}</strong>
              </div>
            </div>
          </div>
          {#if Math.abs(slcSavedByPension) > 10}
            <div class="slc-delta {slcSavedByPension > 0 ? 'slc-saved' : 'slc-cost'}">
              {#if slcSavedByPension > 0}
                Salary sacrifice saves you
                <strong>{fmtGBP(slcSavedByPension)}</strong>
                in student-loan repayments over the loan life.
              {:else}
                Salary sacrifice would <em>increase</em> your total SLC repaid by
                <strong>{fmtGBP(-slcSavedByPension)}</strong> because it pushes
                the loan into wipe territory it would otherwise clear — check
                whether the pension gain is still bigger.
              {/if}
            </div>
          {/if}
        {:else}
          <p class="slc-explain">
            Relief-at-source contributions come out of your NET pay, so they
            don't reduce the salary SLC uses. Your loan trajectory is the
            same whether you contribute or not.
          </p>
          <div class="slc-single">
            {loanWithout.outcome === "cleared"
              ? `Cleared in ${loanWithout.yearsToOutcome} yrs · total repaid ${fmtGBP(loanWithout.totalRepaid)}`
              : `Wiped at yr ${loanWithout.yearsToOutcome} · total repaid ${fmtGBP(loanWithout.totalRepaid)}`}
          </div>
          <p class="slc-tip">
            Switching to salary sacrifice (if your employer offers it) would
            also save 2–8% NI on the contribution — worth asking about.
          </p>
        {/if}
      </div>

      <!-- Rules of thumb -->
      <div class="rules-card card">
        <h3>Rules the maths supports</h3>
        <ol>
          <li>
            <strong>Always max the employer match before any other saving decision.</strong>
            {#if graduate.pensionEmployerMatchRatio >= 1}
              At your current setup this is worth
              {fmtGBP(withScenario.employerMatch)}/yr of free money.
            {/if}
          </li>
          <li>
            <strong>Salary sacrifice beats relief-at-source</strong>
            because it also saves NI. Ask your employer if it's an option.
          </li>
          {#if graduate.salary > 100000 && graduate.salary < 125140}
            <li class="rule-strong">
              <strong>You're in the £100k–£125,140 zone.</strong>
              The next £1 you earn is taxed at ~62% (income tax 40% + PA taper 20% + NI 2%).
              Pension contributions here are unusually valuable — every £1 you
              sacrifice into pension costs you only 38p of net pay.
            </li>
          {/if}
          {#if planData.wipeAfterYears === 40 && loanWithout.outcome === "wiped"}
            <li>
              <strong>Your loan will wipe under Plan 5</strong> regardless of
              pension choice. Overpaying the loan wouldn't help — every spare
              pound should go to pension or ISA.
            </li>
          {/if}
        </ol>
      </div>
    </section>
  </div>
</section>

<style>
  .screen { display: flex; flex-direction: column; gap: 1.25rem; }
  .intro h2 { margin-bottom: 0.35rem; }
  .intro p { color: var(--fg-muted); margin: 0; font-size: 0.95rem; max-width: 68ch; }

  .layout { display: grid; grid-template-columns: 1fr; gap: 1.25rem; }
  @media (min-width: 900px) {
    .layout { grid-template-columns: minmax(300px, 340px) 1fr; }
  }

  .card {
    background: var(--card);
    border: 1px solid var(--card-border);
    border-radius: 0.9rem;
    padding: 1.15rem 1.25rem;
    box-shadow: var(--card-shadow);
  }

  .inputs h3 { margin-bottom: 0.9rem; }
  .section-heading {
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid var(--card-border);
    margin-bottom: 0.9rem;
  }
  .shared-block {
    background: var(--accent-tint);
    border-radius: 0.5rem;
    padding: 0.75rem 0.85rem;
    font-size: 0.88rem;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .shared-block strong { color: var(--accent); }
  .marginal { margin-top: 0.15rem; color: var(--fg-muted); font-size: 0.82rem; }

  .field { display: flex; flex-direction: column; gap: 0.35rem; margin-bottom: 0.85rem; }
  .label {
    font-size: 0.8rem; font-weight: 600; color: var(--fg-muted);
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  input, select {
    font: inherit; font-size: 1rem; padding: 0.55rem 0.75rem;
    border: 1px solid var(--card-border); border-radius: 0.5rem;
    background: var(--bg); color: var(--fg); width: 100%;
    transition: border-color 0.15s, background 0.15s;
  }
  input:focus, select:focus { outline: none; border-color: var(--accent); background: var(--card); }
  .prefix-input, .suffix-input { position: relative; }
  .prefix-input input { padding-left: 1.75rem; }
  .prefix { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--fg-muted); font-weight: 600; pointer-events: none; }
  .suffix-input input { padding-right: 5.5rem; }
  .suffix { position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--fg-muted); font-weight: 600; pointer-events: none; font-size: 0.8rem; }
  .hint { color: var(--fg-subtle); font-size: 0.78rem; }
  .hint-sync {
    color: var(--accent);
    font-size: 0.7rem;
    font-weight: 500;
    opacity: 0.75;
    font-style: italic;
  }

  .output { display: flex; flex-direction: column; gap: 1.25rem; }

  .headline {
    padding: 1.5rem;
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%);
    color: var(--accent-fg); border: none;
  }
  .outcome-label { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.14em; opacity: 0.8; font-weight: 700; }
  .big-figure { font-size: 2.5rem; font-weight: 800; line-height: 1.1; margin: 0.35rem 0 0.15rem; font-variant-numeric: tabular-nums; }
  .secondary-figures { font-size: 0.9rem; opacity: 0.9; }

  .split h3 { margin-bottom: 1rem; }
  .split-legend { list-style: none; padding: 0; margin: 1rem 0 0; display: flex; flex-direction: column; gap: 0.75rem; }
  .legend-below { margin-top: 1rem; }
  .split-legend li { display: flex; gap: 0.65rem; align-items: flex-start; }
  .dot { width: 12px; height: 12px; border-radius: 3px; flex-shrink: 0; margin-top: 0.3rem; }
  .dot-you { background: var(--accent); }
  .dot-employer { background: var(--success); }
  .dot-growth { background: var(--warning); }
  .split-heading { font-weight: 700; color: var(--fg); font-size: 0.95rem; display: flex; align-items: center; gap: 0.4rem; }
  .split-detail { color: var(--fg-muted); font-size: 0.85rem; margin-top: 0.1rem; }
  .tag { font-size: 0.7rem; padding: 0.1em 0.5em; border-radius: 0.35em; font-weight: 700; letter-spacing: 0.02em; }
  .tag-free { background: rgba(30, 126, 94, 0.15); color: var(--success); }
  .return-note {
    margin-top: 1rem; padding-top: 0.9rem;
    border-top: 1px dashed var(--card-border);
    color: var(--fg-muted); font-size: 0.9rem;
  }
  .return-note strong { color: var(--accent); font-size: 1rem; }

  .opp-cost {
    border: 2px solid var(--warning);
    background: rgba(207, 138, 44, 0.05);
  }
  .opp-header {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    align-items: center;
  }
  @media (min-width: 560px) {
    .opp-header { grid-template-columns: auto 1fr; }
  }
  .opp-label { font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.1em; color: var(--warning); font-weight: 700; }
  .opp-value { font-size: 2rem; font-weight: 800; color: var(--warning); line-height: 1.1; font-variant-numeric: tabular-nums; }
  .opp-sub { font-size: 0.88rem; color: var(--fg-muted); margin-top: 0.15rem; }
  .opp-comparison { display: flex; flex-direction: column; gap: 0.4rem; }
  .opp-row {
    display: flex; justify-content: space-between; align-items: baseline;
    padding: 0.4rem 0.7rem; background: var(--bg-2);
    border-radius: 0.4rem; font-size: 0.9rem;
  }
  .opp-note {
    margin-top: 1rem; padding-top: 0.9rem;
    border-top: 1px dashed var(--card-border);
    color: var(--fg); font-size: 0.9rem; line-height: 1.5;
  }

  .opp-split {
    margin-top: 1.15rem;
    padding-top: 1rem;
    border-top: 1px dashed rgba(207, 138, 44, 0.4);
  }
  .opp-split-header {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--fg-muted);
    font-weight: 700;
    margin-bottom: 0.55rem;
  }
  .opp-split-rows {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.9rem;
  }
  .opp-split-row {
    display: flex;
    gap: 0.65rem;
    align-items: flex-start;
  }
  .opp-split-swatch {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    flex-shrink: 0;
    margin-top: 0.28rem;
  }
  .opp-split-swatch-employer { background: var(--danger); }
  .opp-split-swatch-employee { background: var(--warning); }
  .opp-split-row-body { flex: 1; min-width: 0; }
  .opp-split-row-heading {
    font-weight: 700;
    color: var(--fg);
    font-size: 0.92rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
    margin-bottom: 0.15rem;
  }
  .opp-split-row-detail {
    color: var(--fg-muted);
    font-size: 0.85rem;
    line-height: 1.5;
  }
  .opp-split-row-detail strong { color: var(--fg); font-weight: 700; }
  .tag-loss { background: rgba(184, 63, 58, 0.15); color: var(--danger); }
  .tag-neutral { background: var(--bg-2); color: var(--fg-muted); }

  .slc-impact h3 { margin-bottom: 0.65rem; }
  .slc-explain { color: var(--fg-muted); font-size: 0.9rem; margin: 0 0 1rem; line-height: 1.5; }
  .slc-compare {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }
  .slc-col {
    padding: 0.9rem; border-radius: 0.6rem;
    background: var(--bg-2); border: 1px solid var(--card-border);
  }
  .slc-col-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--fg-muted); font-weight: 700; }
  .slc-col-outcome { font-size: 1.05rem; font-weight: 700; margin: 0.3rem 0 0.15rem; }
  .slc-cleared { color: var(--success); }
  .slc-wiped { color: var(--coral); }
  .slc-col-total { font-size: 0.85rem; color: var(--fg-muted); }
  .slc-col-total strong { color: var(--fg); font-weight: 700; }
  .slc-delta {
    margin-top: 0.9rem; padding: 0.75rem 0.9rem;
    border-radius: 0.5rem; font-size: 0.9rem; line-height: 1.5;
  }
  .slc-saved {
    background: rgba(30, 126, 94, 0.08);
    border: 1px solid rgba(30, 126, 94, 0.25);
    color: var(--fg);
  }
  .slc-cost {
    background: rgba(184, 63, 58, 0.08);
    border: 1px solid rgba(184, 63, 58, 0.25);
    color: var(--fg);
  }
  .slc-single {
    padding: 0.9rem; background: var(--bg-2);
    border-radius: 0.6rem; font-weight: 600;
  }
  .slc-tip {
    margin-top: 0.85rem; padding: 0.7rem 0.85rem;
    background: var(--accent-tint);
    border-radius: 0.5rem;
    color: var(--fg); font-size: 0.88rem; line-height: 1.5;
  }

  .rules-card h3 { margin-bottom: 0.65rem; }
  .rules-card ol { padding-left: 1.4rem; margin: 0; }
  .rules-card li { margin-bottom: 0.7rem; line-height: 1.55; font-size: 0.92rem; }
  .rules-card li.rule-strong {
    background: var(--accent-tint);
    padding: 0.65rem 0.85rem;
    border-radius: 0.5rem;
    list-style-position: inside;
    margin-left: -1.4rem;
  }

  @media (max-width: 500px) {
    .slc-compare { grid-template-columns: 1fr; }
    .big-figure { font-size: 2rem; }
    .opp-value { font-size: 1.5rem; }
  }
</style>
